<script setup lang="ts">
import { useAuthStore } from "~/stores/auth/store";
import { ProductTypes } from "~/types";
import AutoComplete, {
  type AutoCompleteChangeEvent,
  type AutoCompleteCompleteEvent,
} from "primevue/autocomplete";
import { repository } from "~/repository";
import { onClickOutside } from "@vueuse/core";

const target = ref(null);
const autoCompleteRef = ref<InstanceType<typeof AutoComplete>>();

onClickOutside(target, () => {
  setTimeout(() => {
    searchDrawerVisible.value = false;
  }, 100);
});

const route = useRoute();

watch(
  () => route.name,
  () => {
    if (route.name != "pokerGO::page:search-results-page") {
      searchQuery.value = "";
    }
    searchDrawerVisible.value = false;
  },
);

const router = useRouter();

const authStore = useAuthStore();
const op = ref();

const items = ref([
  {
    label: "My Account",
    icon: "fa fa-user",
    command: () => {
      navigateTo("/account");
    },
  },
  {
    label: "Notifications",
    icon: "fas fa-bell",
    command: () => {
      navigateTo("/notifications");
    },
  },
  {
    label: "My List",
    icon: "fa fa-heart",
    command: () => {
      navigateTo("/my-list");
    },
  },
  {
    label: "Logout",
    icon: "fa fa-right-from-bracket",
    command: () => {
      authStore.handleLogout();
    },
  },
]);

const mainMenuItems = [
  {
    label: "Home",
    to: "/",
  },
  {
    label: "Schedule",
    to: "/schedule",
  },
  {
    label: "On Demand",
    to: "/on-demand",
  },
  {
    label: "Poker U",
    to: "/training",
  },
  {
    label: "News",
    to: "/news",
  },
];
function toggleMenu(event: any) {
  op.value.toggle(event);
}

function handleMobileMenuToggle() {
  navigateTo("/options");
  if (route.name === "mobile-menu") {
    router.back();
  }
}

const searchDrawerVisible = ref(false);

const showSearchInput = computed(() => {
  return searchDrawerVisible.value;
});

const searchSuggestions = ref<string[]>([]);
const searchQuery = ref((route.query.q as string) || "");
const autoCompleteLoading = ref(false);

async function handleOptionSelect(e: AutoCompleteChangeEvent) {
  await handleSearch(e.value);
}

async function handleAutoComplete({ query }: AutoCompleteCompleteEvent) {
  if (query) {
    autoCompleteLoading.value = true;
    searchSuggestions.value = await repository.search.getSearchAutocomplete({
      query,
    });
    autoCompleteLoading.value = false;
  } else {
    searchSuggestions.value = [];
  }
}

async function handleSearch(query: string) {
  await navigateTo(`/search?q=${query}`);
}

async function handleClickGlass() {
  searchDrawerVisible.value = !searchDrawerVisible.value;
  await nextTick();
  autoCompleteRef?.value?.$el.querySelector("input").focus();
}

const registerLink = computed(() => {
  if (route.query?.ott_code) {
    return `register?ott_code=${route.query?.ott_code}`;
  }
  return "/register/select-plan";
});

const loginLink = computed(() => {
  if (route.query?.ott_code) {
    return `login?ott_code=${route.query?.ott_code}`;
  }
  return "/login";
});
</script>

<template>
  <div class="absolute left-1/2 top-0 w-full -translate-x-1/2 transform">
    <div
      class="mx-auto flex h-20 max-w-[1300px] items-center justify-between rounded-b-2xl bg-primary-950/70 px-5 lg:px-10"
    >
      <div class="flex items-center gap-3">
        <div class="md:hidden" @click="handleMobileMenuToggle">
          <font-awesome-icon :icon="['fas', 'bars']" class="text-xl" />
        </div>
        <div>
          <NuxtLink to="/">
            <img
              src="~assets/images/logos/pokergo-logo.png"
              class="w-32 cursor-pointer lg:w-36"
              alt="pokergo-logo"
            />
          </NuxtLink>
        </div>
      </div>

      <div class="hidden flex-shrink-0 gap-10 lg:flex">
        <NuxtLink
          v-for="(item, index) in mainMenuItems"
          :key="`mainMenuItems_${index}`"
          :to="item.to"
          class="cursor-pointer py-2 hover:scale-105"
          active-class="border-b-4 border-primary-200"
        >
          {{ item.label }}
        </NuxtLink>
      </div>
      <div class="relative flex items-center gap-5 text-sm lg:gap-10">
        <NuxtLink
          class="hidden cursor-pointer hover:scale-105 xl:block"
          to="https://playpokergo.onelink.me/YuJc/nh56lr4w"
          target="_blank"
        >
          <font-awesome-icon :icon="['fas', 'cards']" class="mr-2" />Play now
        </NuxtLink>
        <NuxtLink
          class="hidden cursor-pointer hover:scale-105 xl:block"
          to="https://shop.pokergo.com/"
          target="_blank"
        >
          <font-awesome-icon :icon="['fas', 'cart-shopping']" class="mr-2" />
          Shop now
        </NuxtLink>
        <div
          v-if="showSearchInput"
          ref="target"
          class="absolute -right-0 top-16 flex justify-center lg:-right-5"
        >
          <div class="relative w-[200px] lg:w-96">
            <font-awesome-icon
              :icon="['fas', 'magnifying-glass']"
              class="absolute left-3 top-1/2 z-20 -translate-y-1/2 transform cursor-pointer text-sm text-white"
              @click="handleSearch(searchQuery)"
            />

            <AutoComplete
              ref="autoCompleteRef"
              v-model="searchQuery"
              variant="outlined"
              fluid
              size="small"
              :delay="0"
              :loading="autoCompleteLoading"
              :suggestions="searchSuggestions"
              placeholder="Search for live events, shows, news and more..."
              input-class="relative bg-black text-white text-sm rounded border-none pl-10 focus:ring-0 block"
              complete-on-focus
              @complete="handleAutoComplete"
              @keydown.enter="handleSearch(searchQuery)"
              @option-select="handleOptionSelect"
            />
          </div>
        </div>
        <font-awesome-icon
          :icon="['fas', 'magnifying-glass']"
          class="cursor-pointer text-lg hover:scale-105"
          @click="handleClickGlass"
        />
        <NuxtLink
          v-if="authStore.subscription.interval === ProductTypes.NONE"
          class="hidden cursor-pointer hover:scale-105 lg:block"
          :href="registerLink"
        >
          <Button
            size="small"
            label="Subscribe"
            :pt-options="{ mergeProps: true }"
            :pt="{
              root: '!bg-primary-300/20 hover:!bg-transparent',
            }"
            class="border-primary-200 text-primary-200"
            outlined
          />
        </NuxtLink>
        <NuxtLink
          v-if="!authStore.authenticated"
          class="hover:scale-105"
          :href="loginLink"
        >
          <font-awesome-icon :icon="['fas', 'right-to-bracket']" class="mr-2" />
          Login
        </NuxtLink>
        <div
          v-if="authStore.authenticated"
          class="flex cursor-pointer items-center gap-3"
          @click="toggleMenu"
        >
          <img
            alt="profile-icon"
            src="~assets/images/account-icon-white.svg"
            class="h-8 w-8"
          />

          <font-awesome-icon :icon="['fas', 'caret-down']" />
        </div>
      </div>
    </div>

    <Menu id="overlay_menu" ref="op" :model="items" :popup="true">
      <template #itemicon="item">
        <font-awesome-icon :icon="item.item.icon" class="mr-2" />
      </template>
    </Menu>
  </div>
</template>

<style scoped></style>
